'use client';

import type { IconAnimateMode } from '@magicschool/spellbook/components/Icon';
import type { FocusEvent, MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

// Method shorthand (bivariant) lets callers with element-specific handlers (e.g. MouseEvent<HTMLButtonElement>)
// satisfy this constraint without any. Property-typed functions would require any here due to contravariance.
type InteractCallerProps = {
  onMouseEnter?(e: MouseEvent<Element>): void;
  onMouseLeave?(e: MouseEvent<Element>): void;
  onFocus?(e: FocusEvent<Element>): void;
  onBlur?(e: FocusEvent<Element>): void;
};

/**
 * Tracks hover and keyboard focus interaction state on a container element and derives the
 * `animating` flag consumed by `<Icon animate="hover" animating={animating} />`.
 *
 * Pass `getInteractProps(props)` to the container to wire up the four interaction handlers and
 * merge them with any caller-supplied handlers. Pass the returned `animating` value to `Icon`.
 * When `animate` is not set, `animating` is always `undefined` and `getInteractProps` is a no-op.
 *
 * Disable animation for disabled components by resolving `animate` before calling the hook:
 * ```tsx
 * const resolvedAnimate = isDisabled ? undefined : animate;
 * const { animating, getInteractProps } = useAnimatedIcon(resolvedAnimate);
 * ```
 *
 * @example
 * ```tsx
 * const resolvedAnimate = isDisabled ? undefined : animate;
 * const { animating, getInteractProps } = useAnimatedIcon(resolvedAnimate);
 *
 * return (
 *   <button {...props} {...getInteractProps(props)}>
 *     <Icon name="star" animate={resolvedAnimate} animating={animating} />
 *   </button>
 * );
 * ```
 */
export function useAnimatedIcon(animate: IconAnimateMode): {
  /** Pass as `animating` to `<Icon animate="hover" animating={animating} />`. Always `undefined` when `animate` is not set. */
  animating: boolean | undefined;
  /**
   * Merges the caller's `onMouseEnter`, `onMouseLeave`, `onFocus`, and `onBlur` with the
   * animation start/stop logic. Spread the result onto the interactive container after the
   * caller's props so the merged handlers win:
   *
   * ```tsx
   * <Comp {...props} {...getInteractProps(props)} />
   * ```
   *
   * Pass no args (or `{}`) when there are no caller handlers to merge, e.g. on a wrapper div.
   * No-op when `animate` is not set.
   */
  getInteractProps: (callerProps?: InteractCallerProps) => InteractCallerProps;
} {
  const [isInteracted, setIsInteracted] = useState(false);

  // Reset stale interaction state when the animate mode changes so a prior `true`
  // value doesn't trigger a spurious animation on the next 'hover' activation.
  // biome-ignore lint/correctness/useExhaustiveDependencies: animate is in deps to trigger on mode change; the callback intentionally ignores the new value
  useEffect(() => {
    setIsInteracted(false);
  }, [animate]);

  const getInteractProps = useMemo(
    () =>
      (callerProps: InteractCallerProps = {}) => {
        // When animate is not set, return {} so the caller's own handlers pass through unmodified
        // (consumers spread {...props} {...getInteractProps(props)}, so {} is a true no-op).
        if (!animate) return {};
        return {
          onMouseEnter: (e: MouseEvent<Element>) => {
            setIsInteracted(true);
            callerProps.onMouseEnter?.(e);
          },
          onMouseLeave: (e: MouseEvent<Element>) => {
            setIsInteracted(false);
            callerProps.onMouseLeave?.(e);
          },
          onFocus: (e: FocusEvent<Element>) => {
            setIsInteracted(true);
            callerProps.onFocus?.(e);
          },
          onBlur: (e: FocusEvent<Element>) => {
            // Skip if focus moved to another element inside the same container (e.g. anchor → chevron
            // in a collapsible NavLink row). React's onBlur uses bubbling focusout, so both elements
            // fire blur on the wrapper even though the user never left the row visually.
            if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) {
              setIsInteracted(false);
            }
            callerProps.onBlur?.(e);
          },
        };
      },
    [animate],
  );

  return {
    animating: animate ? isInteracted : undefined,
    getInteractProps,
  };
}
